import { ApiService } from './api-services/ApiService';
import type { Appointment } from '../types/Appointment';
import type { CreateAppointmentData, UpdateAppointmentData } from '../types/Appointment';

class AppointmentService {
  private get api() {
    return ApiService.getInstance();
  }

  // Récupérer tous les rendez-vous via l'API directe
  async getAppointments(filters?: {
    patientId?: number;
    doctorId?: number;
    status?: string;
    date?: string;
  }): Promise<Appointment[]> {
    const params = new URLSearchParams();

    if (filters?.patientId) params.append('userId', filters.patientId.toString());
    if (filters?.doctorId) params.append('userId', filters.doctorId.toString());
    if (filters?.status) params.append('status', filters.status);
    if (filters?.date) params.append('dateFrom', filters.date);

    const queryString = params.toString();
    const url = `/api/appointments${queryString ? `?${queryString}` : ''}`;

    const response = await this.api.getExtracted<unknown>(url);
    
    if (response && typeof response === 'object' && 'appointments' in response) {
      return (response as { appointments: Appointment[] }).appointments;
    }
    
    return (Array.isArray(response) ? response : []) as Appointment[];
  }

  // Récupérer un rendez-vous par ID
  async getAppointmentById(id: string): Promise<Appointment | undefined> {
    return await this.api.getExtracted<Appointment>(`/api/appointments/${id}`);
  }

  // Créer un nouveau rendez-vous
  async createAppointment(appointmentData: CreateAppointmentData): Promise<Appointment | undefined> {
    return await this.api.postExtracted<Appointment>('/api/appointments', appointmentData);
  }

  // Mettre à jour un rendez-vous
  async updateAppointment(id: string, updateData: UpdateAppointmentData): Promise<Appointment | undefined> {
    return await this.api.putExtracted<Appointment>(`/api/appointments/${id}`, updateData);
  }

  // Confirmer un rendez-vous
  async confirmAppointment(id: string): Promise<Appointment | undefined> {
    return await this.api.putExtracted<Appointment>(`/api/appointments/${id}/confirm`);
  }

  // Décliner/Rejeter une demande de rendez-vous en attente
  async declineAppointment(id: string, reason?: string): Promise<Appointment | undefined> {
    return await this.api.putExtracted<Appointment>(`/api/appointments/${id}/decline`, { reason });
  }

  // Reprogrammer un rendez-vous (route dédiée)
  async rescheduleAppointment(id: string, date: string, time: string): Promise<Appointment | undefined> {
    return await this.api.putExtracted<Appointment>(`/api/appointments/${id}/reschedule`, { date, time });
  }

  // Annuler un rendez-vous
  async cancelAppointment(id: string, reason?: string): Promise<void> {
    await this.api.putExtracted(`/api/appointments/${id}/cancel`, { reason });
  }

  // Marquer un rendez-vous comme "no show"
  async markNoShow(id: string): Promise<Appointment | undefined> {
    return await this.api.putExtracted<Appointment>(`/api/appointments/${id}/no-show`);
  }
}

export default new AppointmentService();
